import { useNavigate } from "react-router-dom";
import MenuTop from "../../../../components/menu/top/menu-top";
import { useEffect, useState, useCallback } from "react";
import Loader from "../../../../components/loader/loader";
import { ListComponent } from "../../../../components/list/list";
import MenuContext, { DISPLAY_TYPE } from "../../../../components/menu/context/menu-context";
import { useAlert } from "../../../../components/alert/alert";
import { SEVERITY } from "../../../../shared/interfaces/alert";
import { 
    fetchBeneficiaryProjectsService, 
    deleteBeneficiaryProjectService 
} from "../service/service";
import Icons from "../../../../components/icons/iconts";

export function ListBeneficiaryProject() {
    const navigate = useNavigate();
    const { showAlert } = useAlert();
    const [isLoading, setIsLoading] = useState(false);
    const [beneficiaryProjects, setBeneficiaryProjects] = useState<any[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [showInactive, setShowInactive] = useState<boolean>(false);
    const [lastUpdateTime, setLastUpdateTime] = useState<string>('');

    const fetchBeneficiaryProjects = useCallback(async () => {
        try {
            setIsLoading(true);
            
            const data = await fetchBeneficiaryProjectsService();
            
            // Garantir que sempre temos um array
            const safeData = Array.isArray(data) ? data : [];

            // Formatar dados para a ListComponent
            const formattedData = safeData.map((item: any, index: number) => {
                // Determinar nomes do beneficiário e projeto
                const beneficiaryName = item.beneficiary?.name || 
                                      item.beneficiary_name || 
                                      `Beneficiário ID: ${item.beneficiaryId || 'N/A'}`;
                
                const projectName = item.project?.name || 
                                  item.project_name_full || 
                                  item.project_name || 
                                  `Projeto ID: ${item.projectId || 'N/A'}`;
                
                // Calcular custo mensal em formato de exibição
                let monthlyCostDisplay = 0;
                if (item.monthly_cost_display) {
                    monthlyCostDisplay = item.monthly_cost_display;
                } else if (item.monthly_cost) {
                    // Se o valor está em centavos, dividir por 100
                    monthlyCostDisplay = item.monthly_cost > 1000 ? item.monthly_cost / 100 : item.monthly_cost;
                }
                
                return {
                    id: item.id || `temp-${Date.now()}-${index}`,
                    name: `${beneficiaryName} → ${projectName}`, // Mudando de 'title' para 'name' para compatibilidade com ListComponent
                    title: `${beneficiaryName} → ${projectName}`,
                    subtitle: `${monthlyCostDisplay.toLocaleString('pt-BR', { 
                        style: 'currency', 
                        currency: 'BRL' 
                    })} / mês`,
                    description: item.project_start_date && item.project_end_date ? 
                        `${new Date(item.project_start_date).toLocaleDateString('pt-BR')} até ${new Date(item.project_end_date).toLocaleDateString('pt-BR')}` :
                        'Datas não definidas',
                    status: item.is_active ? 'Ativo' : 'Inativo',
                    statusColor: item.is_active ? '#2e7d32' : '#d32f2f',
                    beneficiary: item.beneficiary,
                    project: item.project,
                    beneficiaryName,
                    projectName,
                    monthly_cost_display: monthlyCostDisplay,
                    project_start_date: item.project_start_date,
                    project_end_date: item.project_end_date,
                    description_text: item.description || '',
                    is_active: item.is_active ?? true,
                    created_at: item.created_at || item.createdAt,
                    companyId: item.companyId
                };
            });

            setBeneficiaryProjects(formattedData);
            setLastUpdateTime(new Date().toLocaleTimeString('pt-BR'));
            
            if (formattedData.length === 0) {
                showAlert('Nenhum vínculo encontrado. A tabela pode estar vazia ou você pode não ter permissão para visualizar os dados.', SEVERITY.INFO);
            }
            
        } catch (error: any) {
            console.error('❌ Erro ao buscar vínculos:', error);
            showAlert(
                error.message || 'Erro ao carregar vínculos beneficiário-projeto', 
                SEVERITY.ERROR
            );
            setBeneficiaryProjects([]);
        } finally {
            setIsLoading(false);
        }
    }, []); // Removendo showAlert da dependência para evitar loop

    // Filtrar dados baseado na busca e status
    const filteredData = beneficiaryProjects.filter(item => {
        const searchText = searchTerm.toLowerCase();
        const matchesSearch = !searchTerm || 
            item.title.toLowerCase().includes(searchText) ||
            item.beneficiaryName.toLowerCase().includes(searchText) ||
            item.projectName.toLowerCase().includes(searchText) ||
            item.description_text?.toLowerCase().includes(searchText);
        
        const matchesStatus = showInactive || item.is_active;
        
        return matchesSearch && matchesStatus;
    });

    // Função para excluir vínculo (sem dependências para evitar loop)
    const handleDelete = useCallback(async (id: string) => {
        const item = beneficiaryProjects.find(bp => bp.id === id);
        const confirmMessage = item ? 
            `Tem certeza que deseja excluir o vínculo:\n"${item.title}"?` :
            'Tem certeza que deseja excluir este vínculo?';
            
        if (window.confirm(confirmMessage)) {
            try {
                setIsLoading(true);
                await deleteBeneficiaryProjectService(id);
                showAlert('Vínculo excluído com sucesso!', SEVERITY.SUCCESS);
                // Recarregar lista diretamente em vez de chamar a função
                const data = await fetchBeneficiaryProjectsService();
                const safeData = Array.isArray(data) ? data : [];
                
                const formattedData = safeData.map((item: any, index: number) => {
                    const beneficiaryName = item.beneficiary?.name || 
                                          item.beneficiary_name || 
                                          `Beneficiário ID: ${item.beneficiaryId || 'N/A'}`;
                    
                    const projectName = item.project?.name || 
                                      item.project_name_full || 
                                      item.project_name || 
                                      `Projeto ID: ${item.projectId || 'N/A'}`;
                    
                    let monthlyCostDisplay = 0;
                    if (item.monthly_cost_display) {
                        monthlyCostDisplay = item.monthly_cost_display;
                    } else if (item.monthly_cost) {
                        monthlyCostDisplay = item.monthly_cost > 1000 ? item.monthly_cost / 100 : item.monthly_cost;
                    }
                    
                    return {
                        id: item.id || `temp-${Date.now()}-${index}`,
                        name: `${beneficiaryName} → ${projectName}`,
                        title: `${beneficiaryName} → ${projectName}`,
                        subtitle: `${monthlyCostDisplay.toLocaleString('pt-BR', { 
                            style: 'currency', 
                            currency: 'BRL' 
                        })} / mês`,
                        description: item.project_start_date && item.project_end_date ? 
                            `📅 ${new Date(item.project_start_date).toLocaleDateString('pt-BR')} até ${new Date(item.project_end_date).toLocaleDateString('pt-BR')}` :
                            '📅 Datas não definidas',
                        status: item.is_active ? '✅ Ativo' : '❌ Inativo',
                        statusColor: item.is_active ? '#2e7d32' : '#d32f2f',
                        beneficiary: item.beneficiary,
                        project: item.project,
                        beneficiaryName,
                        projectName,
                        monthly_cost_display: monthlyCostDisplay,
                        project_start_date: item.project_start_date,
                        project_end_date: item.project_end_date,
                        description_text: item.description || '',
                        is_active: item.is_active ?? true,
                        created_at: item.created_at || item.createdAt,
                        companyId: item.companyId
                    };
                });
                
                setBeneficiaryProjects(formattedData);
                setLastUpdateTime(new Date().toLocaleTimeString('pt-BR'));
            } catch (error: any) {
                showAlert(
                    error.message || 'Erro ao excluir vínculo', 
                    SEVERITY.ERROR
                );
            } finally {
                setIsLoading(false);
            }
        }
    }, [beneficiaryProjects, showAlert]);

    // Estatísticas dos dados
    const stats = {
        total: beneficiaryProjects.length,
        active: beneficiaryProjects.filter(item => item.is_active).length,
        inactive: beneficiaryProjects.filter(item => !item.is_active).length,
        filtered: filteredData.length,
        totalCost: filteredData.reduce((sum, item) => sum + (item.monthly_cost_display || 0), 0)
    };

    useEffect(() => {
        fetchBeneficiaryProjects();
    }, []); // Array vazio para executar apenas uma vez

    return (
        <div className="container-client">
            <MenuTop />
            {isLoading && <Loader />}
            
            <div className='container-client__content'>
                <div className="container-client__content__title">
                    <h1 className="container-client__content__title-text">
                        Vínculos Beneficiário com o Projeto
                    </h1>
                    <MenuContext 
                        display={DISPLAY_TYPE.INLINE} 
                        clickedAdd={() => navigate('/beneficiario-projeto/adicionar')}
                    />
                </div>
                
                {/* Filtros e controles */}
                <div style={{ 
                    display: 'flex', 
                    gap: '12px', 
                    marginBottom: '16px',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    padding: '12px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '6px'
                }}>
                    {/* Campo de busca */}
                    <input
                        type="text"
                        placeholder="Buscar por beneficiário, projeto ou descrição..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{
                            padding: '8px 12px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            minWidth: '300px',
                            fontSize: '14px',
                            flex: '1'
                        }}
                    />
                    
                    {/* Toggle para mostrar inativos */}
                    <label style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '6px', 
                        fontSize: '14px',
                        cursor: 'pointer',
                        userSelect: 'none'
                    }}>
                        <input
                            type="checkbox"
                            checked={showInactive}
                            onChange={(e) => setShowInactive(e.target.checked)}
                        />
                        Mostrar inativos
                    </label>
                    
                    {/* Botões de navegação */}
                    <button 
                        onClick={() => navigate('/beneficiario-projeto/mensais')}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: '#4db0b1',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                        }}
                    >
                        <Icons iconName="CalendarMonth" fontSize={16} color="white" />
                        Ver Beneficiários do Mês
                    </button>
                </div>

                {/* Informações de resumo da busca */}
                {(searchTerm || !showInactive) && filteredData.length !== beneficiaryProjects.length && (
                    <div style={{
                        padding: '12px',
                        backgroundColor: '#fff3cd',
                        borderRadius: '6px',
                        marginBottom: '16px',
                        fontSize: '14px',
                        color: '#856404',
                        border: '1px solid #ffeaa7'
                    }}>
                        <strong>Filtros aplicados:</strong> Mostrando {filteredData.length} de {beneficiaryProjects.length} vínculos
                        {searchTerm && <span> | Busca: "{searchTerm}"</span>}
                        {!showInactive && <span> | Apenas ativos</span>}
                        <button 
                            onClick={() => {
                                setSearchTerm('');
                                setShowInactive(false);
                            }}
                            style={{
                                marginLeft: '12px',
                                padding: '4px 8px',
                                backgroundColor: '#6c757d',
                                color: 'white',
                                border: 'none',
                                borderRadius: '3px',
                                cursor: 'pointer',
                                fontSize: '12px'
                            }}
                        >
                            Limpar Filtros
                        </button>
                    </div>
                )}
                
                {filteredData.length > 0 ? (
                    <ListComponent 
                        data={filteredData} 
                        handleRedirect={(id: string) => navigate(`/beneficiario-projeto/editar/${id}`)}
                    />
                ) : (
                    !isLoading && (
                        <div className="empty-message" style={{ 
                            textAlign: 'center', 
                            padding: '40px',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '8px',
                            border: '2px dashed #dee2e6'
                        }}>
                            {beneficiaryProjects.length === 0 ? (
                                <div>
                                    <Icons iconName="Assignment" fontSize={48} color="#495057" />
                                    <h3 style={{ margin: '16px 0 8px 0', color: '#495057' }}>
                                        Nenhum vínculo encontrado
                                    </h3>
                                    <p style={{ fontSize: '14px', color: '#6c757d', marginBottom: '16px' }}>
                                        Ainda não há vínculos entre beneficiários e projetos cadastrados.
                                        <br />
                                        Crie o primeiro vínculo para começar.
                                    </p>
                                </div>
                            ) : (
                                <div>
                                    <Icons iconName="Search" fontSize={48} color="#495057" />
                                    <h3 style={{ margin: '16px 0 8px 0', color: '#495057' }}>
                                        Nenhum resultado encontrado
                                    </h3>
                                    <p style={{ fontSize: '14px', color: '#6c757d', marginBottom: '16px' }}>
                                        Nenhum vínculo corresponde aos filtros aplicados.
                                        <br />
                                        Tente ajustar os critérios de busca.
                                    </p>
                                </div>
                            )}
                            
                            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                                <button 
                                    onClick={() => navigate('/beneficiario-projeto/adicionar')}
                                    style={{
                                        padding: '12px 24px',
                                        backgroundColor: '#4db0b1',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        fontWeight: 'bold',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px'
                                    }}
                                >
                                    <Icons iconName="Add" fontSize={18} color="white" />
                                    Criar Novo Vínculo
                                </button>
                                
                                {beneficiaryProjects.length === 0 && (
                                    <>
                                        <button 
                                            onClick={() => navigate('/beneficiarios')}
                                            style={{
                                                padding: '12px 24px',
                                                backgroundColor: '#6c757d',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '6px',
                                                cursor: 'pointer',
                                                fontSize: '14px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px'
                                            }}
                                        >
                                            <Icons iconName="People" fontSize={18} color="white" />
                                            Ver Beneficiários
                                        </button>
                                        
                                        <button 
                                            onClick={() => navigate('/projetos')}
                                            style={{
                                                padding: '12px 24px',
                                                backgroundColor: '#6c757d',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '6px',
                                                cursor: 'pointer',
                                                fontSize: '14px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px'
                                            }}
                                        >
                                            <Icons iconName="Engineering" fontSize={18} color="white" />
                                            Ver Projetos
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    )
                )}
            </div>
        </div>
    );
}